import { AssetType } from "common/constants";
import { ActionPermissionsObject } from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import { UIActionType, isUIActionRestricted } from "js/core/uiActionsRestrictors";

export enum FileSource {
    Local = "local",
    Google = "google",
    OneDrive = "onedrive",
    Dropbox = "dropbox",
    Box = "box",
    Bynder = "bynder",
    Frontify = "frontify",
    Vimeo = "vimeo",
    Youtube = "youtube"
}

export enum FileType {
    Image = "image",
    Video = "video",
    Spreadsheet = "spreadsheet",
    Document = "document",
    Presentation = "presentation"
}

interface FileTypeDefinition {
    extensions: string[];
    mimeTypes: string[];
    maxFileSize?: {
        size: number;
        label: string;
    };
    supportedSources: FileSource[];
}

const cloudFileSources = [FileSource.Google, FileSource.OneDrive, FileSource.Dropbox, FileSource.Box];
const damFileSources = [FileSource.Bynder, FileSource.Frontify];

export const fileTypeDefinitions: Record<FileType, FileTypeDefinition> = {
    [FileType.Image]: {
        extensions: ["jpg", "jpeg", "png", "gif", "svg", "webp", "avif"],
        mimeTypes: ["image/jpeg", "image/png", "image/gif", "image/svg", "image/svg+xml", "image/webp", "image/avif"],
        maxFileSize: {
            size: 1024 * 1024 * 50,
            label: "50mb"
        },
        supportedSources: [...cloudFileSources, ...damFileSources]
    },
    [FileType.Video]: {
        extensions: ["mp4", "webm", "mov", "avi", "wmv", "mkv"],
        mimeTypes: [
            "video/mp4",
            "video/webm",
            "video/quicktime",
            "video/mov",
            "video/x-msvideo",
            "video/avi",
            "video/x-ms-wmv",
            "video/wmv",
            "video/x-matroska",
            "video/mkv"
        ],
        maxFileSize: {
            size: 1024 * 1024 * 500,
            label: "500mb"
        },
        supportedSources: [...cloudFileSources, ...damFileSources, FileSource.Vimeo, FileSource.Youtube]
    },
    [FileType.Spreadsheet]: {
        extensions: ["xls", "xlsx", "csv"],
        mimeTypes: [
            "text/csv",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.google-apps.spreadsheet"
        ],
        supportedSources: cloudFileSources
    },
    [FileType.Document]: {
        extensions: ["doc", "docx", "txt", "pdf"],
        mimeTypes: [
            "text/plain",
            "application/pdf",
            "application/msword",
            "application/vnd.google-apps.document",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ],
        supportedSources: cloudFileSources
    },
    [FileType.Presentation]: {
        extensions: ["ppt", "pptx", "pps", "pot", "potx", "ppsx", "pptm", "ppsm", "potm"],
        mimeTypes: [
            "application/vnd.ms-powerpoint",
            "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        ],
        supportedSources: cloudFileSources
    }
};

export const allowedExtensionsPerFileType = Object.fromEntries(
    Object.entries(fileTypeDefinitions).map(([fileType, definition]) => [fileType, definition.extensions])
) as Record<FileType, string[]>;

// Retain the existing misspelled export while callers migrate to the canonical name.
export const allowedExtentionsPerFileType = allowedExtensionsPerFileType;

export const maxFileSizePerFileType = Object.fromEntries(
    Object.entries(fileTypeDefinitions)
        .filter(([, definition]) => definition.maxFileSize)
        .map(([fileType, definition]) => [fileType, definition.maxFileSize])
) as Partial<Record<FileType, NonNullable<FileTypeDefinition["maxFileSize"]>>>;

export const aiContextFileTypes = [FileType.Document, FileType.Presentation, FileType.Spreadsheet];

export function getAllowedFileSources(fileType: FileType): FileSource[] {
    const canUseIntegrations = WorkspaceController.actionPermissions[ActionPermissionsObject.INTEGRATIONS].use;

    const fileSources: FileSource[] = [];
    const supportedSources = fileTypeDefinitions[fileType].supportedSources;

    if (canUseIntegrations) {
        fileSources.push(...supportedSources.filter(source => ![FileSource.Vimeo, FileSource.Youtube].includes(source)));
    }
    const supportsHostedVideo = supportedSources.some(source => [FileSource.Vimeo, FileSource.Youtube].includes(source));
    if (supportsHostedVideo && !isUIActionRestricted(UIActionType.ADD_ASSET, { assetType: AssetType.VIDEO, source: "hosted" })) {
        fileSources.push(...supportedSources.filter(source => [FileSource.Vimeo, FileSource.Youtube].includes(source)));
    }

    return fileSources;
}

export function fileTypesToExtensions(fileTypes: FileType[]): string[] {
    return [...new Set(fileTypes.flatMap(fileType => fileTypeDefinitions[fileType].extensions))];
}

export function fileTypesToMimeTypes(fileTypes: FileType[]): string[] {
    return [...new Set(fileTypes.flatMap(fileType => fileTypeDefinitions[fileType].mimeTypes))];
}

export function fileTypesToAccept(fileTypes: FileType[]): string[] {
    return [
        ...fileTypesToExtensions(fileTypes).map(extension => `.${extension}`),
        ...fileTypesToMimeTypes(fileTypes)
    ];
}

export function validateFileTypeAndSize(file: File, allowedFileTypes: FileType[] = []) {
    const mimeType = file.type?.trim().toLowerCase();
    const fileExtension = file.name?.split(".").pop()?.toLowerCase();
    const matchedFileType = allowedFileTypes.find(fileType => {
        const definition = fileTypeDefinitions[fileType];
        const matchesMimeType = mimeType && definition.mimeTypes.some(allowedMimeType => allowedMimeType.toLowerCase() === mimeType);
        const matchesExtension = fileExtension && definition.extensions.some(allowedExtension => allowedExtension.toLowerCase() === fileExtension);
        return matchesMimeType || matchesExtension;
    });

    if (!matchedFileType) {
        const uploadedFileType = mimeType?.split("/")[0] || "file";
        const uploadedFileExtension = mimeType?.split("/")[1] || fileExtension || "unknown";
        return `Uploaded ${uploadedFileType} extension ${uploadedFileExtension} is not supported`;
    }

    const maxFileSize = fileTypeDefinitions[matchedFileType].maxFileSize;
    if (maxFileSize && file.size > maxFileSize.size) {
        return `Uploaded ${matchedFileType} should have max size ${maxFileSize.label}`;
    }
}
